import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { MAX_ESIGNATURE_BULK_CREATE } from 'config/Vars'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import UserSelector from 'modules/core/components/UserSelector'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { useCreateEsignatureRequests } from 'modules/esignatures/hooks/requests/useCreateEsignatureRequests'

interface Props {
  document: Document
  onClose: () => void
  contractVersions: ContractVersions
}

const EsignatureModal = ({ document, onClose, contractVersions }: Props) => {
  const employee = document.employee
  const { create } = useCreateEsignatureRequests()
  const { access: currentAccess } = useCurrentSession()

  const [selectedIds, setSelectedIds] = React.useState(employee ? [employee.accessId] : [])

  const flashMaxError = () => {
    app.setFlash({
      type: 'wrong',
      message: i18n.t('documents.modal.esignature.error_max_selected_users', {
        count: MAX_ESIGNATURE_BULK_CREATE,
      }),
    })
  }

  const onSelectMultiple = (userIds: number[]) => {
    if (userIds.length >= MAX_ESIGNATURE_BULK_CREATE) return flashMaxError()

    setSelectedIds(userIds)
  }

  const onSelect = (userId: number | null | undefined) => {
    if (!userId) return setSelectedIds([])
    if (!selectedIds.includes(userId) && selectedIds.length >= MAX_ESIGNATURE_BULK_CREATE) {
      return flashMaxError()
    }

    setSelectedIds(xor(selectedIds, [userId]))
  }

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      app.setProgress({
        message: i18n.t('common.loading'),
      })

      await create({
        documentId: document.id,
        requesterId: currentAccess.id,
        signeesId: selectedIds,
      })

      app.setProgress(null)

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      closePortal()
    },
    {
      successFlash: false,
    }
  )

  const validUsersForEsignature = users.models.filter((user) => {
    return user.isActive && user.attributes.confirmed
  })

  return (
    <Modal
      illustration="esignature"
      title={i18n.t('documents.modal.esignature.modal.title')}
      description={i18n.t('documents.modal.esignature.modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button submit disabled={running || !selectedIds.length}>
          {i18n.tp('documents.modal.esignature.modal.submit', {
            count: selectedIds.length,
          })}
        </Button>
      }
    >
      <UserSelector
        onSelect={onSelect}
        onSelectMultiple={onSelectMultiple}
        selectedIds={selectedIds}
        users={validUsersForEsignature}
        contractVersions={contractVersions}
      />
    </Modal>
  )
}

export default observer(EsignatureModal)
